/**
 * Per-turn entity-catalog derivation — THE integration seam with the parser
 * (architecture.md §4.4, §7.1 `entities/`).
 *
 * @remarks
 * The parser owns language → structured proposal; the engine owns what
 * exists. Each turn this module answers "what is currently referable" as
 * data: items placed in the active room (minus what the player has removed),
 * items carried, and the pack's ambient items — D19b's one documented line:
 * ambient items are appended to every turn's catalog, because placement
 * means LOCATION and EURYDICE does not have one.
 *
 * Visibility conditions gate membership (an item whose
 * `roomVisibilityCondition` is unmet is not merely un-actionable — it is NOT
 * REFERABLE at all, so the parser cannot leak its existence through a
 * clarification question).
 *
 * D134 replaced the three-tier salience policy (inventory > room portables >
 * fixtures) with a LOCATION on every entry and a preference on the VERB: a
 * salience tier was "take the bar" preferring the bar in hand, which was the
 * right answer to the wrong verb, and the same rule made `examine` pick
 * silently between two things a player could see. Now every real thing has
 * one salience and a location, `take` reaches for what is present, `drop`
 * for what is carried, and a verb with no preference asks. Salience keeps
 * only its D41/D63 job: the room itself and the hands sit below every thing.
 *
 * Catalog order is deterministic: inventory in carry order, then room
 * placements in authored order, then what was dropped here, then (D232) the
 * actors addressable from here in declared order, then ambient in declared
 * order — and every item entry's ref is its item id re-branded
 * ({@link entityRefForItem}); an actor's is prefixed ({@link entityRefForActor}).
 *
 * @packageDocumentation
 */

import type { ActorDefinition, ItemDefinition, ItemId, RoomDefinition, RuntimeState } from '@orpheus/content-schema';
import type { EntityLocation, EntityRef, ParseCandidateEntity } from '@orpheus/parser-engine';

import type { EngineGameBundle, IndexedPack, LivePage } from '../bundle/engine-game-bundle.js';

import {
	descriptionWordsOfActor,
	descriptionWordsOfItem,
	descriptionWordsOfRoom,
	livePageOfActor,
	livePageOfItem,
	livePageOfRoom,
	requireItem,
} from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { firstMatchingVariant } from '../conditions/first-matching-variant.js';
import { actorsAddressableHere, isActorPresent } from './actor-presence.js';
import { everyDoorWordOf, liveDoorWordsOf, withoutDoorWords } from './door-state-words.js';
import { actorIdForEntityRef, BODY_REF, ENGINE_ROOM_WORDS, entityRefForActor, entityRefForItem, entityRefForRoom, itemIdForEntityRef } from './entity-ref.js';
import { isConcealedItemFound, isContainerOpen, isShutSeeThrough } from './item-state.js';
import { OBJECT_FORM_OF, pronounAntecedentsOf, withPartnerPronounReserved } from './partner-pronoun.js';

/**
 * Salience for every real THING, wherever it is (D134): location is a fact
 * the verb weighs, not a rank the catalog imposes.
 */
export const SALIENCE_THING = 1;

/** The room itself (D41) — below every thing in it, so named things always win. */
export const SALIENCE_ROOM_ITSELF = 0.5;

/**
 * Derive the parser catalog for the current turn.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The referable entities, in deterministic order
 */
export const deriveEntityCatalog = (bundle: EngineGameBundle, state: RuntimeState): readonly ParseCandidateEntity[] => {
	const catalog: ParseCandidateEntity[] = [];

	// Carried items first — highest salience, carry order.
	for (const itemId of state.world.inventoryItemIds) {
		const item = requireItem(bundle, itemId);

		if (isVisible(item.inventoryVisibilityCondition, bundle, state)) {
			catalog.push(toLiveCatalogEntry(item, 'carried', bundle, state));
		}
	}

	// Active-room placements next — authored order, minus the player's deltas.
	const placements = bundle.indexes.initialItemIdsByRoomId.get(state.progression.activeRoomId) ?? [];

	for (const itemId of placements) {
		if (isAuthoredPlacementPresent(bundle, state, itemId)) {
			const item = requireItem(bundle, itemId);

			catalog.push(toLiveCatalogEntry(item, item.isPortable ? 'present' : 'fixture', bundle, state));
		}
	}

	// R25: things the player set down HERE join next.
	appendDroppedHere(catalog, bundle, state);

	// D232: the actors the player can address from here — present, or heard
	// through their `audibleFrom` — in the pack's declared order, with thing
	// salience: `examine the woman` and `ask the woman about the mask` resolve
	// like any noun, and the parser never learns the word "actor". After the
	// room's things because a person is named less often than the thing in
	// their hands is; before the ambient voice because a body in the room
	// outranks a voice in the air.
	for (const actor of actorsAddressableHere(bundle, state)) {
		catalog.push(toLiveActorCatalogEntry(actor, bundle, state));
	}

	// Ambient items last — in scope everywhere, every turn (D19b).
	for (const itemId of bundle.contentPack.ambientItemIds) {
		const item = requireItem(bundle, itemId);

		if (isVisible(item.roomVisibilityCondition, bundle, state)) {
			catalog.push(toLiveCatalogEntry(item, 'fixture', bundle, state));
		}
	}

	// D50, D239: what the accessible containers are holding — and what those
	// hold, to a fixed point.
	appendReachableContents(catalog, bundle, state);

	// D41: the room itself, always referable and always last — "search the
	// room", "look at here". Lowest salience, so a real thing named `area`
	// still wins over the walls around it. Defensive like the rest of this
	// walk: a state pointing at a room the pack does not define contributes
	// nothing rather than throwing.
	const room = bundle.indexes.roomsById.get(state.progression.activeRoomId);

	if (room !== undefined) {
		catalog.push({ ...toRoomCatalogEntry(bundle, room), descriptionWords: livePageWordsOf(livePageOfRoom(bundle, room.roomId), bundle, state) });
	}

	catalog.push(BODY_ENTRY);

	// D235: while the player is talking with somebody, that body's pronoun is its alone (`partner-pronoun.ts`).
	return withPartnerPronounReserved(catalog, pronounAntecedentsOf(bundle, state));
};

/**
 * D63: the hands ride at the very end, below even the room — they resolve
 * only when nothing else claims the words, and they exist so the engine's
 * own "Open it with what?" accepts the answer a person gives.
 */
export const BODY_ENTRY: ParseCandidateEntity = {
	adjectives: ['bare', 'empty'],
	// D245 (A4): the body answers to the player's words for themselves, as aliases — a thing a pack calls
	// `yourself` (a noun) outranks the hands, and a topic keyed `me` still meets the body by the word typed (`liveTopicFor`).
	aliases: ['bare hands', 'my hands', 'your hands', 'my hand', 'me', 'myself', 'yourself'],
	entityRef: BODY_REF,
	location: 'body',
	nouns: ['hands', 'hand'],
	salience: 0,
};

/**
 * R25: append the items the player set down in the active room — placed by
 * the player, not the author, so they are referable unconditionally (you can
 * see the thing you just put on the floor; `roomVisibilityCondition` guards
 * authored placements, never droppage). Record insertion order keeps this
 * walk deterministic, so replays fold identically.
 *
 * @param catalog - The catalog under construction (appended in place)
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 */
const appendDroppedHere = (catalog: ParseCandidateEntity[], bundle: EngineGameBundle, state: RuntimeState): void => {
	for (const [droppedItemId, droppedRoomId] of Object.entries(state.world.droppedItemLocations)) {
		if (droppedRoomId === state.progression.activeRoomId) {
			catalog.push(toLiveCatalogEntry(requireItem(bundle, droppedItemId as ItemId), 'present', bundle, state));
		}
	}
};

/**
 * D50, D239: append what the accessible containers hold — and what THOSE
 * hold, to a fixed point.
 *
 * @remarks
 * Appended after their containers, so a contained thing never outranks the
 * thing holding it. D50 walked a SNAPSHOT of the catalog, which made nesting
 * structurally out of scope: a compartment inside a compartment could be
 * found and opened, and what it held could never be named. D239 walks the
 * catalog AS IT GROWS (an array's iterator visits what is pushed during the
 * walk), so a chip behind the false back of the slate in the player's hand
 * is referable the moment every door and every concealment above it has
 * given way — and not before, because each level is reached only from an
 * entry the walk already admitted, through that container's own door and
 * concealment ({@link isReachableInside}). Each level is still found by its
 * own act: searching the slate finds the false back, and only searching the
 * false back finds what is hidden behind it.
 *
 * A carried container is walked like any other (it is in the catalog, first),
 * so a find in the hands stays in the thing that held it: it travels with the
 * slate, is set down with it, and comes into the hands only when it is taken.
 * D240: so is a BODY within reach — what TALLY carries is in the catalog
 * wherever TALLY stands with the player, and a compartment it carries opens
 * onto the next level exactly as a thing's does.
 *
 * Nothing is listed twice. The loader refuses a containment cycle and a thing
 * both contained and placed, so on a loaded pack every contained thing has
 * one way in; the listed set keeps a hand-built bundle from repeating an
 * entry, or from walking a cycle forever.
 *
 * @param catalog - The catalog under construction (appended in place)
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 */
const appendReachableContents = (catalog: ParseCandidateEntity[], bundle: EngineGameBundle, state: RuntimeState): void => {
	const listedEntityRefs = new Set(catalog.map((entry) => entry.entityRef));

	for (const containerEntry of catalog) {
		for (const containedItemId of reachableContentsOf(containerEntry.entityRef, bundle, state)) {
			const containedEntityRef = entityRefForItem(containedItemId);
			const containedItem = requireItem(bundle, containedItemId);

			if (!listedEntityRefs.has(containedEntityRef) && isStillInPlace(state, containedItemId)) {
				listedEntityRefs.add(containedEntityRef);
				catalog.push({ ...toLiveCatalogEntry(containedItem, containedItem.isPortable ? 'present' : 'fixture', bundle, state), containerRef: containerEntry.entityRef });
			}
		}
	}
};

/**
 * What one catalog entry holds that the player can name this turn (D50,
 * D239, D240) — a thing's contents through its door and its concealment, or
 * a BODY's pockets while the body is within reach.
 *
 * @remarks
 * D240 retired D232's recorded line ("an actor's pockets are fiction until a
 * pack needs them"; the Meridian needs one). A body's contents are reached
 * only while the body is PRESENT: an actor heard through its `audibleFrom` is
 * in the catalog to be spoken to, and lends the hands nothing, so what it
 * carries stays out of reach with it. A body has no door; concealment reads
 * the one predicate a thing's does ({@link isConcealedItemFound}). The room
 * and the hands hold nothing.
 *
 * @param holderRef - An entry the walk has admitted, or a thing or body searched
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The ids it holds that pass every gate, in declaration order
 */
const reachableContentsOf = (holderRef: EntityRef, bundle: EngineGameBundle, state: RuntimeState): readonly ItemId[] => {
	const carryingActorId = actorIdForEntityRef(holderRef);

	if (carryingActorId !== undefined) {
		return isActorPresent(bundle, state, carryingActorId)
			? (bundle.indexes.carriedItemIdsByActorId.get(carryingActorId) ?? []).filter((carriedItemId) =>
				isVisibleWhereFound(requireItem(bundle, carriedItemId), bundle, state),
			)
			: [];
	}

	const containerItemId = itemIdForEntityRef(holderRef);

	return (bundle.indexes.containedItemIdsByContainerId.get(containerItemId) ?? []).filter((containedItemId) =>
		isReachableInside(requireItem(bundle, containedItemId), containerItemId, bundle, state),
	);
};

/**
 * D247: what a thing or a body still holds in plain sight — past its door,
 * found if it was hidden, and neither taken nor gone from the world — in
 * declaration order. What a search names when it turns up nothing hidden.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param holderRef - The thing or body searched
 * @returns The ids still on or in it, there to be seen
 */
export const contentsInSightOf = (bundle: EngineGameBundle, state: RuntimeState, holderRef: EntityRef): readonly ItemId[] =>
	reachableContentsOf(holderRef, bundle, state).filter((containedItemId) => isStillInPlace(state, containedItemId));

/**
 * Can the player refer to this contained thing yet (D50)?
 *
 * @remarks
 * Two gates, and they are different questions:
 *
 * - **The door.** A container declaring `openable` hides its contents until it
 *   is open. One with no door — a collapsed shelf, a spilled tray — hides
 *   nothing, which is what the prose means by "spilled its contents". D253:
 *   nor does a shut door of mesh or glass (`isSeeThrough`) — what is behind
 *   it is in sight, and the reach walk keeps it out of the hands.
 * - **Concealment.** A thing marked `concealed` waits to be SEARCHED, not
 *   merely opened. That is the whole difference between looking and searching,
 *   and it is why a sheaf can sit behind a pipe that has no door at all. D239:
 *   read through {@link isConcealedItemFound}, the predicate `item_found`
 *   reads, so a condition and the catalog agree about every find.
 *
 * A thing that is found and has its own door is a compartment: found is not
 * open (D239). Its door is the next level's first gate, asked when the walk
 * reaches what the compartment holds.
 *
 * This replaces the flag-as-visibility trick, which needed one flag per secret
 * and could never express "in an open box, still not obvious".
 *
 * @param containedItem - The thing inside
 * @param containerItemId - What it is inside
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns Whether it belongs in this turn's catalog
 */
const isReachableInside = (
	containedItem: ItemDefinition,
	containerItemId: ItemId,
	bundle: EngineGameBundle,
	state: RuntimeState,
): boolean => {
	const container = requireItem(bundle, containerItemId);

	if (!isContainerOpen(container, state) && !isShutSeeThrough(container, state)) {
		return false;
	}

	return isVisibleWhereFound(containedItem, bundle, state);
};

/**
 * The gates past the door (D50, D240): found, if it is concealed, and its own
 * visibility condition — what a thing inside a thing and a thing on a body
 * both still have to pass.
 *
 * @param containedItem - The thing inside, or carried
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns Whether it may be named, its container being reachable
 */
const isVisibleWhereFound = (containedItem: ItemDefinition, bundle: EngineGameBundle, state: RuntimeState): boolean =>
	(containedItem.containedIn?.concealed !== true || isConcealedItemFound(containedItem, state)) &&
	isVisible(containedItem.roomVisibilityCondition, bundle, state);


/**
 * Project a room into the catalog — the engine's own room words (D41,
 * {@link ENGINE_ROOM_WORDS}), plus the room's authored name — so "search the
 * muster room" works as well as "search here" — plus, since D137, the room's
 * own `nouns` (merged, never replacing). — the one the player stands in per turn,
 * or the one a static walk asks about (D207).
 *
 * @param bundle - The pack and its indexes
 * @param room - The room
 * @returns The room's catalog entry
 */
export const toRoomCatalogEntry = (bundle: IndexedPack, room: RoomDefinition): ParseCandidateEntity => ({
	adjectives: [],
	aliases: [room.name],
	descriptionWords: descriptionWordsOfRoom(bundle, room.roomId),
	entityRef: entityRefForRoom(room.roomId),
	location: 'room',
	nouns: [...ENGINE_ROOM_WORDS, ...room.nouns],
	salience: SALIENCE_ROOM_ITSELF,
});

/**
 * Is an item the author placed in a room still THERE, as of this turn (D230)?
 *
 * @remarks
 * `itemPlacements` is a room's STARTING state, and three things turn it into
 * the present: the player may have taken the thing (it is in the hands), an
 * effect may have removed it from the world (`removedRoomItemIds`), and its
 * `roomVisibilityCondition` may not hold yet — an unmet condition means the
 * thing is not merely un-actionable but NOT REFERABLE, so nothing may leak
 * its existence. The catalog has always read a placement through all three;
 * the room survey (`handleSearchRoom`) read the placements raw, and in the
 * Orpheus Protocol's lit substation offered the seam the breakers had not
 * yet revealed — "Worth going through properly: seam." followed by "You see
 * no seam here." (Calder's POC2). One predicate now, so every reader of a
 * placement sees the same room.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param itemId - An item some room's `itemPlacements` names
 * @returns Whether the player could name it where it was placed, this turn
 */
export const isAuthoredPlacementPresent = (bundle: EngineGameBundle, state: RuntimeState, itemId: ItemId): boolean =>
	isStillInPlace(state, itemId) && isVisible(requireItem(bundle, itemId).roomVisibilityCondition, bundle, state);

/**
 * Is a thing still where the pack put it — not taken (in the hands, or set
 * down since, which a take records) and not removed from the world?
 *
 * @param state - The live runtime state
 * @param itemId - The thing
 * @returns Whether it is still in its authored place
 */
const isStillInPlace = (state: RuntimeState, itemId: ItemId): boolean =>
	!state.world.removedRoomItemIds.includes(itemId) && !state.world.inventoryItemIds.includes(itemId);

/** An absent visibility condition means "always visible". */
const isVisible = (
	condition: ItemDefinition['roomVisibilityCondition'],
	bundle: EngineGameBundle,
	state: RuntimeState,
): boolean => condition === undefined || evaluateCondition(condition, bundle, state);

/**
 * Project one item definition into the parser's catalog shape
 * (architecture.md §4.4).
 *
 * @remarks
 * D53: the item's own NAME joins its aliases, which is what rooms have always
 * done ({@link toRoomCatalogEntry}) and what items were missing. The engine
 * PRINTS that name — "You go through the desk drawer, and find the plant
 * food." — so a name the catalog will not answer to is the engine promising a
 * phrase and then refusing it, which is the prose-promise rule turned on the
 * engine's own output.
 *
 * Found because `item_plant_food` declares the noun `food` and the aliases
 * `carton` / `feed` / `fertilizer`, so `take the food` worked and `take the
 * plant food` — the phrase the reveal line had just used — did not. Every
 * author will assume the name works; now it does, rather than every author
 * having to remember to repeat it.
 *
 * Added only when the name is not ALREADY reachable as a noun or an alias,
 * which is the common case: `item_pry_bar` repeats "pry bar" in its aliases,
 * and any item whose name is simply its noun ("lamp") needs nothing. A
 * doubled entry would skew nothing today but is exactly the sort of thing
 * scoring later grows to care about.
 *
 * D205 widens the same rule from the name to the PAGE: the words the item's
 * own description uses ride along (`descriptionWords`, indexed once at load),
 * so a modifier the prose offered — "an old man", "the white crosses" — is a
 * modifier the parser accepts without an author declaring it an adjective.
 *
 * @param item - The item being projected
 * @param location - Where it is, for the verb to weigh (D134)
 * @param bundle - The pack and its indexes, for the page's words
 * @returns The parser-facing candidate
 */
export const toCatalogEntry = (item: ItemDefinition, location: EntityLocation, bundle: IndexedPack): ParseCandidateEntity => ({
	// D216: the static view answers a door's words in either position — it asks whether a phrase could EVER resolve.
	adjectives: [...withoutDoorWords(item, item.adjectives), ...everyDoorWordOf(item)],
	aliases:
		item.aliases.includes(item.name) || item.nouns.includes(item.name) ? item.aliases : [...item.aliases, item.name],
	descriptionWords: withoutDoorWords(item, descriptionWordsOfItem(bundle, item.itemId)),
	entityRef: entityRefForItem(item.itemId),
	location,
	nouns: item.nouns,
	salience: SALIENCE_THING,
});

/**
 * Project one item into THIS TURN's catalog (D216): the static entry, with
 * the door's words its live state earns and the page's words from the
 * readings that are showing.
 *
 * @remarks
 * Declared adjectives are the first rung the resolver tries, and a door's
 * position belongs there: `examine open locker` must pick locker eleven on
 * its state alone once the bar has forced it, not on whether its prose
 * happens to repeat the word. See `door-state-words.ts` for why the page and
 * the adjectives lose their say over a door's words.
 *
 * @param item - The item being projected
 * @param location - Where it is, for the verb to weigh (D134)
 * @param bundle - The loaded bundle, for the conditions its readings name
 * @param state - The live runtime state
 * @returns The parser-facing candidate, as of this turn
 */
export const toLiveCatalogEntry = (
	item: ItemDefinition,
	location: EntityLocation,
	bundle: EngineGameBundle,
	state: RuntimeState,
): ParseCandidateEntity => ({
	...toCatalogEntry(item, location, bundle),
	adjectives: [...withoutDoorWords(item, item.adjectives), ...liveDoorWordsOf(item, state)],
	descriptionWords: withoutDoorWords(item, livePageWordsOf(livePageOfItem(bundle, item.itemId), bundle, state)),
});

/**
 * Project one actor into the catalog's STATIC shape (D232) — the words it
 * answers to in any turn: its nouns and aliases (and its name, the D53 rule
 * items have), its adjectives, and every word its page could ever say.
 * Location `fixture`: an actor is never carried and never taken, and
 * `fixture` is what every verb that prefers the present reaches for (D134).
 *
 * @param actor - The actor being projected
 * @param bundle - The pack and its indexes, for the page's words
 * @returns The parser-facing candidate
 */
export const toActorCatalogEntry = (actor: ActorDefinition, bundle: IndexedPack): ParseCandidateEntity => ({
	adjectives: actor.adjectives,
	aliases: actor.aliases.includes(actor.name) || actor.nouns.includes(actor.name) ? actor.aliases : [...actor.aliases, actor.name],
	descriptionWords: descriptionWordsOfActor(bundle, actor.actorId),
	entityRef: entityRefForActor(actor.actorId),
	location: 'fixture',
	nouns: actor.nouns,
	// D245: the body answers to its own pronoun alone — `it` is never the woman just spoken of.
	...(actor.pronoun === undefined ? {} : { pronoun: OBJECT_FORM_OF[actor.pronoun] }),
	salience: SALIENCE_THING,
});

/**
 * Project one actor into THIS TURN's catalog (D232): the static entry, with
 * the page's words from the readings that are showing (D216's rule) — and
 * (D252) marked as somebody an order may be given to: every body in this
 * turn's catalog is one the player can speak to from here, and an order is
 * speech.
 *
 * @param actor - The actor being projected
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The parser-facing candidate, as of this turn
 */
const toLiveActorCatalogEntry = (actor: ActorDefinition, bundle: EngineGameBundle, state: RuntimeState): ParseCandidateEntity => ({
	...toActorCatalogEntry(actor, bundle),
	descriptionWords: livePageWordsOf(livePageOfActor(bundle, actor.actorId), bundle, state),
	isAddressable: true,
});

/**
 * The words a page says as of this turn (D216): what every reading says, and
 * from each conditional text, the reading the describer would show now —
 * first match wins, the fallback when nothing matches.
 *
 * @param page - The page as indexed at load
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The words, each once
 */
const livePageWordsOf = (page: LivePage, bundle: EngineGameBundle, state: RuntimeState): readonly string[] => [
	...new Set([
		...page.steadyWords,
		...page.readings.flatMap(
			(reading) => firstMatchingVariant(reading.variants, (variant) => variant.condition, bundle, state)?.words ?? reading.fallbackWords,
		),
	]),
];
